import { Injectable, Injector } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FeatureKeys, FeatureService } from "company/components/features/feature.service";
import { IAccountingContact } from "rl-common/services/accounting-processes/models/i-accounting-contact";
import { IAccountingProcess } from "rl-common/services/accounting-processes/models/i-accounting-process";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessAccountGroupRule } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule";
import { IAccountingProcessAccountGroupRuleSet } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule-set";
import { AddContactAccountsModalAdapter } from "../components/accounting-processes/add-contact-accounts-modal/add-contact-accounts-modal.adapter";
import { BuildAccountGroupRuleModalAdapter } from "../components/accounting-processes/build-account-group-rule-modal/build-account-group-rule-modal.adapter";
import { BuildAccountGroupRuleSetModalAdapter } from "../components/accounting-processes/build-account-group-rule-set-modal/build-account-group-rule-set-modal.adapter";
import { BuildAccountingProcessModalAdapter } from "../components/accounting-processes/build-accounting-process-modal/build-accounting-process-modal.adapter";
import { CopyAccountingProcessModalAdapter } from "../components/accounting-processes/copy-accounting-process-modal/copy-accounting-process-modal.adapter";
import { FFBuildAccountingProcessModalAdapter } from "../components/accounting-processes/ff-build-accounting-process-modal/ff-build-accounting-process-modal.adapter";

@Injectable({ providedIn: "root" })
export class CompanyModalService {

	amendmentsEnabled = toSignal(this._featureService.isEnabled$(FeatureKeys.Amendments));

	constructor(
		private readonly _addContactAccountsModalAdapter: AddContactAccountsModalAdapter,
		private readonly _buildAccountingProcessModalAdapter: BuildAccountingProcessModalAdapter,
		private readonly _ffBuildAccountingProcessModalAdapter: FFBuildAccountingProcessModalAdapter,
		private readonly _copyAccountingProcessModalAdapter: CopyAccountingProcessModalAdapter,
		private readonly _buildAccountGroupRuleModalAdapter: BuildAccountGroupRuleModalAdapter,
		private readonly _buildAccountGroupRuleSetModalAdapter: BuildAccountGroupRuleSetModalAdapter,
		private readonly _featureService: FeatureService
	) {
	}

	addContactAccounts(contacts: IAccountingContact[], injector: Injector) {
		return this._addContactAccountsModalAdapter.openModal(component => {
			component.existingContacts = contacts;
			return component.onComplete;
		}, { injector, backdrop: "static" });
	}

	buildAccountingProcess(selectedContact: IAccountingContact, injector: Injector, accountingProcessId: string = null, createOperation: boolean = false, editOperationId: string = null) {
		const useAmendments = this.amendmentsEnabled();
		if (useAmendments) {
			return this._ffBuildAccountingProcessModalAdapter.openModal(component => {
				component.accountingProcessId = accountingProcessId;
				component.selectedContact = selectedContact;
				component.createOperation = createOperation;
				component.editOperationId = editOperationId;
				return component.onClose;
			}, { injector, backdrop: "static" });
		} else {
			return this._buildAccountingProcessModalAdapter.openModal(component => {
				component.accountingProcessId = accountingProcessId;
				component.selectedContact = selectedContact;
				component.createOperation = createOperation;
				component.editOperationId = editOperationId;
				return component.onClose;
			}, { injector, backdrop: "static" });
		}
	}

	copyAccountingProcess(currentContactId: number, selectedContact: IAccountingContact, accountingProcess: IAccountingProcess, injector: Injector) {
		return this._copyAccountingProcessModalAdapter.openModal(component => {
			component.currentContactId = currentContactId;
			component.selectedContact = selectedContact;
			component.accountingProcess = accountingProcess;
			return component.onComplete;
		}, { injector, backdrop: "static" });
	}

	buildAccountGroupRule(accountGroup: IAccountingProcessAccountGroup, ruleSet: IAccountingProcessAccountGroupRuleSet, accountGroupRule: IAccountingProcessAccountGroupRule, injector: Injector) {
		return this._buildAccountGroupRuleModalAdapter.openModal(component => {
			component.accountGroup = accountGroup;
			component.ruleSet = ruleSet;
			component.accountGroupRule = accountGroupRule;
			return component.onClose;
		}, { injector, backdrop: "static" });
	}

	buildAccountGroupRuleSet(accounts: IAccountingProcessAccount[], accountGroup: IAccountingProcessAccountGroup, accountGroupRuleSet: IAccountingProcessAccountGroupRuleSet, maxSequenceNumber: number, existingNames: string[], injector: Injector) {
		return this._buildAccountGroupRuleSetModalAdapter.openModal(component => {
			component.accounts = accounts;
			component.accountGroup = accountGroup;
			component.accountGroupRuleSet = accountGroupRuleSet;
			component.maxSequenceNumber = maxSequenceNumber;
			component.existingNames = existingNames;
			return component.onClose;
		}, { injector, backdrop: "static" });
	}
}